import type { i18n as I18nInstance } from 'i18next';

/**
 * SSR 로 안 실린 i18n 네임스페이스를 런타임에 채운다.
 *
 * `next-i18next.config.js` 에는 클라이언트 HTTP backend 가 없으므로,
 * 페이지의 `serverSideTranslations` 목록에 없는 ns 는 키 문자열이 그대로 노출된다.
 * 무거운 ns 를 전 페이지에 싣는 대신, 그게 필요한 UI 를 열기 직전에만 이걸로 채운다.
 *
 * **all-or-nothing 이다.** 하나라도 실패하면 throw 한다 — 부분 로드 상태로 화면을 열면
 * 번역이 반만 되는 정도가 아니라, 그 문구가 그대로 서버에 저장되는 경로가 있다
 * (보드 생성 시 제목·공지·그룹명이 i18n 문자열이다).
 */
export async function ensureNamespaces(
  i18n: I18nInstance,
  namespaces: readonly string[],
): Promise<void> {
  const language = i18n.language;
  const missing = namespaces.filter(
    ns => !i18n.hasResourceBundle(language, ns),
  );

  if (missing.length === 0) {
    return;
  }

  const bundles = await Promise.all(
    missing.map(async ns => {
      const response = await fetch(`/locales/${language}/${ns}.json`);

      if (!response.ok) {
        throw new Error(
          `i18n 네임스페이스 로드 실패: ${language}/${ns} (${response.status})`,
        );
      }

      return [ns, await response.json()] as const;
    }),
  );

  // 전부 받아온 뒤에 한꺼번에 등록한다 — 중간에 하나가 실패하면 아무것도 등록하지 않는다.
  bundles.forEach(([ns, resources]) => {
    i18n.addResourceBundle(language, ns, resources, true, true);
  });
}
